import { useState } from 'react';
import { SEGMENTS_MANAGER_ERRORS } from '~/modules/covenants/CovenantsSegmentsManager/constants.ts';
import type { TApiSupervisorSegment } from '~/modules/covenants/types.ts';
import { getCovenantsRequestErrorMessage } from '~/modules/covenants/utils.ts';
import {
  createSupervisorSegmentSupervisor,
  deleteSupervisorSegmentSupervisor,
  fetchSupervisorSegments,
} from '~/store/covenants/thunks/supervisorSegmentThunks.ts';
import type { AppDispatch } from '~/store/store.ts';
import type { UseFormReturn } from 'react-hook-form';
import {
  buildSupervisorEmailFieldName,
  findSupervisorSegment,
  hasDuplicateSupervisorEmail,
  type TCovenantsSegmentsManagerFormValues,
} from '~/modules/covenants/CovenantsSegmentsManager/utils.ts';

type TUseSegmentsManagerActionsParams = {
  dispatch: AppDispatch;
  formMethods: UseFormReturn<TCovenantsSegmentsManagerFormValues>;
  segments: TApiSupervisorSegment[];
  onError: (message: string) => void;
};

const useSegmentsManagerActions = ({
  dispatch,
  formMethods,
  segments,
  onError,
}: TUseSegmentsManagerActionsParams) => {
  const [addingSegmentId, setAddingSegmentId] = useState<string | null>(null);
  const [deletingSupervisorId, setDeletingSupervisorId] = useState<string | null>(null);

  const refreshSegments = async () => {
    await dispatch(fetchSupervisorSegments()).unwrap();
  };

  const handleAddSupervisor = async (segmentId: string) => {
    const fieldName = buildSupervisorEmailFieldName(segmentId);
    const isFieldValid = await formMethods.trigger(fieldName);
    const email = formMethods.getValues(fieldName)?.trim() ?? '';
    const segment = findSupervisorSegment(segments, segmentId);

    if (!email || !isFieldValid) {
      return;
    }

    if (hasDuplicateSupervisorEmail(segment, email)) {
      formMethods.setError(fieldName, {
        type: 'duplicate',
        message: SEGMENTS_MANAGER_ERRORS.duplicateEmail,
      });
      return;
    }

    setAddingSegmentId(segmentId);

    try {
      await dispatch(createSupervisorSegmentSupervisor({ segmentId, email })).unwrap();
      await refreshSegments();
      formMethods.setValue(fieldName, '', {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      formMethods.clearErrors(fieldName);
    } catch (error) {
      onError(getCovenantsRequestErrorMessage(error));
    } finally {
      setAddingSegmentId(null);
    }
  };

  const handleDeleteSupervisor = async (segmentId: string, supervisorId: string) => {
    setDeletingSupervisorId(supervisorId);

    try {
      await dispatch(deleteSupervisorSegmentSupervisor({ segmentId, supervisorId })).unwrap();
      await refreshSegments();
    } catch (error) {
      onError(getCovenantsRequestErrorMessage(error));
    } finally {
      setDeletingSupervisorId(null);
    }
  };

  return {
    addingSegmentId,
    deletingSupervisorId,
    handleAddSupervisor,
    handleDeleteSupervisor,
  };
};

export default useSegmentsManagerActions;
